const SABRE_CHAR_MAP = new Map([
  ['\uFFFD', '\u00A4'],
  ['\u00AF', '\u00A4'],
  ['\u00AE', '\u00A4'],
  ['\u00A4', '\u00A4'],
  ['\u00A5', '\u00A5'],
  ['\u00B6', '\u00B6'],
  ['\u001D', '\u009D'],
  ['\u009D', '\u009D'],
  ['\u0001', '\u0001'],
  ['\u0000', ''],
]);

const SPECIAL_CHAR_MEANINGS = new Map([
  ['\uFFFD', 'replacement_char_possible_sabre_delimiter'],
  ['\u00AF', 'sabre_start_delimiter_normalized'],
  ['\u00AE', 'sabre_end_delimiter_normalized'],
  ['\u00A4', 'sabre_delimiter'],
  ['\u00A5', 'sabre_separator_preserved'],
  ['\u00B6', 'sabre_special_separator'],
  ['\u001D', 'group_separator_normalized_to_sabre_marker'],
  ['\u009D', 'sabre_marker_preserved'],
  ['\u0001', 'control_char_preserved'],
  ['\u0000', 'null_char_removed_from_normalized'],
]);

const SABRE_SPECIAL_SEQUENCE = '\u00A4\u00A5\u00B6\u00A4';

function getCodePoint(char) {
  return `U+${char.codePointAt(0).toString(16).toUpperCase().padStart(4, '0')}`;
}

function isControlChar(char) {
  const codePoint = char.codePointAt(0);

  return (codePoint >= 0x0000 && codePoint <= 0x001f) || (codePoint >= 0x007f && codePoint <= 0x009f);
}

function createSpecialChar(char, index, normalizedTo, meaning) {
  return {
    char,
    codePoint: getCodePoint(char),
    index,
    meaning,
    normalizedTo,
  };
}

function collectSpecialSequences(normalized = '') {
  const sequences = [];
  let index = normalized.indexOf(SABRE_SPECIAL_SEQUENCE);

  while (index !== -1) {
    sequences.push({
      sequence: SABRE_SPECIAL_SEQUENCE,
      meaning: 'sabre_special_sequence',
      visible: false,
      index,
    });
    index = normalized.indexOf(SABRE_SPECIAL_SEQUENCE, index + SABRE_SPECIAL_SEQUENCE.length);
  }

  return sequences;
}

function createUnknownControlDiagnostic(lineNumber, raw, char) {
  return {
    type: 'diagnostic',
    level: 'warning',
    code: 'UNKNOWN_CONTROL_CHAR',
    message: 'Caracter de control no reconocido detectado en linea SST.',
    line: lineNumber,
    char,
    codePoint: getCodePoint(char),
    raw,
  };
}

function createReplacementCharDiagnostic(lineNumber) {
  return {
    type: 'diagnostic',
    level: 'info',
    code: 'REPLACEMENT_CHAR_DETECTED',
    message: 'Se detecto caracter de reemplazo. Se tratara como posible delimitador Sabre.',
    line: lineNumber,
    normalizedTo: '\u00A4',
  };
}

export function scanSstLine(rawLine = '', lineNumber = 0) {
  let normalized = '';
  const specialChars = [];
  const diagnostics = [];

  for (let index = 0; index < rawLine.length; index += 1) {
    const char = rawLine[index];

    if (SABRE_CHAR_MAP.has(char)) {
      const normalizedTo = SABRE_CHAR_MAP.get(char);
      normalized += normalizedTo;
      specialChars.push(
        createSpecialChar(char, index, normalizedTo, SPECIAL_CHAR_MEANINGS.get(char) || 'known_sabre_char_normalized')
      );

      if (char === '\uFFFD') {
        diagnostics.push(createReplacementCharDiagnostic(lineNumber));
      }

      continue;
    }

    if (isControlChar(char)) {
      normalized += char;
      specialChars.push(createSpecialChar(char, index, char, 'unknown_control_char_preserved'));
      diagnostics.push(createUnknownControlDiagnostic(lineNumber, rawLine, char));
      continue;
    }

    normalized += char;
  }

  const specialSequences = collectSpecialSequences(normalized);

  return {
    line: lineNumber,
    raw: rawLine,
    normalized,
    hasSpecialChars: specialChars.length > 0,
    specialChars,
    hasSpecialSequences: specialSequences.length > 0,
    specialSequences,
    diagnostics,
  };
}

export function createSpecialCharReport(scannedLines = []) {
  const charsByCodePoint = new Map();
  const sequencesByName = new Map();
  const linesWithSpecialChars = scannedLines.filter((line) => line.hasSpecialChars);

  scannedLines.forEach((line) => {
    (line.specialSequences || []).forEach((specialSequence) => {
      const current = sequencesByName.get(specialSequence.sequence);

      if (!current) {
        sequencesByName.set(specialSequence.sequence, {
          sequence: specialSequence.sequence,
          meaning: specialSequence.meaning,
          visible: false,
          count: 1,
        });
        return;
      }

      current.count += 1;
    });
  });

  linesWithSpecialChars.forEach((line) => {
    line.specialChars.forEach((specialChar) => {
      const key = specialChar.codePoint;
      const current = charsByCodePoint.get(key);

      if (!current) {
        charsByCodePoint.set(key, {
          char: specialChar.char,
          codePoint: specialChar.codePoint,
          count: 1,
          normalizedTo: specialChar.normalizedTo,
          meaning: specialChar.meaning,
        });
        return;
      }

      current.count += 1;
    });
  });

  return {
    totalLinesWithSpecialChars: linesWithSpecialChars.length,
    chars: Array.from(charsByCodePoint.values()),
    sequences: Array.from(sequencesByName.values()),
  };
}

export function scanSstContent(content = '') {
  const rawLines = content.split(/\r?\n/);
  const lines = rawLines.map((rawLine, index) => scanSstLine(rawLine, index + 1));
  const diagnostics = lines.flatMap((line) => line.diagnostics);

  return {
    lines,
    diagnostics,
    report: createSpecialCharReport(lines),
  };
}
